import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Fullscreen, FullscreenExit } from '@material-ui/icons';
import cx from 'classnames';
import { scroller } from 'react-scroll';

import { isIOSDevice, isScreenRotated } from '../../utils/helpers';
import { useEventListener } from '../../utils/hooks';
import useVerse from '../../utils/hooks/useVerse';
import { CustomButton } from '../UI/CustomButton';
import useStyles from './styles';

interface VideoPlayerProps {
  id?: string;
  src?: string;
  className?: string;
  duration?: number;
  title?: string;
  expertName?: string;
  verseId?: string | null;
  videoUrl?: string | null;
  isModal?: boolean;
  bumperVideoUrl?: string | null;
  onToggleFullscreen?: (isFullscreen: boolean) => void;
  isFullScreen?: boolean;
  additionalIframeUrlParams?: string;
}

const exitFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  }
};

const VideoPlayer = React.forwardRef<HTMLFrameElement, VideoPlayerProps>(
  (
    {
      verseId,
      className = '',
      duration = null,
      expertName,
      title,
      id,
      videoUrl,
      isModal = false,
      bumperVideoUrl,
      onToggleFullscreen,
      isFullScreen,
      additionalIframeUrlParams = '',
    },
    ref,
  ) => {
    const isIOS = isIOSDevice();
    const [isIosFullScreen, setIsIosFullScreen] = useState<boolean>(false);
    const [isRotated, setIsRotated] = useState<boolean>(false);
    const isRotatedIOS = isIOS && isRotated;
    const classes = useStyles(isModal, isRotatedIOS)();

    const [showBumper, setShowBumper] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    // @ts-ignore
    const contentWindow = ref?.current?.contentWindow || iframeRef?.current?.contentWindow;
    const { goToChapter, changeVideoDuration } = useVerse(contentWindow);

    const url = verseId
      ? `${process.env.REACT_APP_VERSE_PLAYER_URL}/stories/${verseId}-xunlocked/?embed&controls=2&sharing=0&covertitle=0${additionalIframeUrlParams}`
      : videoUrl || '';

    useEffect(() => {
      setIsRotated(isScreenRotated());
    }, []);

    //used to save fullscreen state on screen rotate that cause modal remount
    useEffect(() => {
      if (typeof isFullScreen === 'boolean') {
        setIsIosFullScreen(isFullScreen);
      }
    }, [isFullScreen]);

    const exitIosFullscreen = useCallback(() => {
      setIsIosFullScreen(false);
      onToggleFullscreen?.(false);
      if (window?.userengage) {
        window.userengage('widget.show');
      }
      //scroll to video while exiting from fullscreen mode
      if (isIosFullScreen) {
        scroller.scrollTo('playerWrap', {
          duration: 1000,
          smooth: 'easeInOutQuart',
          offset: -80,
        });
      }
    }, [isIosFullScreen, onToggleFullscreen]);

    const toggleIosFullScreen = useCallback(
      (directMode?: boolean) => {
        if (directMode === false || (directMode === undefined && isIosFullScreen)) {
          exitIosFullscreen();
        } else if ((directMode === undefined && !isIosFullScreen) || directMode === true) {
          setIsIosFullScreen(true);
          onToggleFullscreen?.(true);
          if (window?.userengage) {
            window.userengage('widget.hide');
          }
        }
      },
      [isIosFullScreen, exitIosFullscreen, onToggleFullscreen],
    );

    const handleVideoWatched = useCallback(() => {
      if (bumperVideoUrl) {
        setShowBumper(true);
      }

      if (isIosFullScreen && exitIosFullscreen) {
        exitIosFullscreen();
      } else {
        exitFullscreen();
      }
    }, [bumperVideoUrl, exitIosFullscreen, isIosFullScreen]);

    const handleRestartVideo = (e) => {
      e.stopPropagation();
      goToChapter('0');
      setShowBumper(false);
    };

    const handleResizeIosFullscreen = useCallback(() => {
      if (isIOSDevice()) {
        const isRotated = isScreenRotated();
        setIsRotated(isRotated);
        toggleIosFullScreen(isRotated);
      }
    }, [toggleIosFullScreen]);

    useEventListener('orientationchange', handleResizeIosFullscreen);
    useEventListener('last-chapter-finished', handleVideoWatched);

    useEffect(() => {
      if (duration) {
        changeVideoDuration(duration, title, expertName);
      }
    }, [title, expertName, duration, changeVideoDuration]);

    return (
      <div className={cx(classes.wrapper, isIosFullScreen && classes.fullScreen)}>
        {isIosFullScreen ? (
          <FullscreenExit
            onClick={(e) => {
              e.stopPropagation();
              toggleIosFullScreen();
            }}
            className={classes.fullScreenIcon}
          />
        ) : (
          <Fullscreen
            onClick={(e) => {
              e.stopPropagation();
              toggleIosFullScreen();
            }}
            className={classes.fullScreenIcon}
          />
        )}
        {bumperVideoUrl && (
          <div className={cx(classes.bumper, showBumper && classes.active)}>
            <video className={classes.video} autoPlay muted loop playsInline>
              <source src={bumperVideoUrl} />
            </video>
            <CustomButton onClick={handleRestartVideo} btnClass={classes.btn}>
              Restart
            </CustomButton>
          </div>
        )}
        {/*eslint-disable-next-line*/}
        <iframe
          id={id}
          className={className}
          src={url}
          frameBorder="0"
          allow="autoplay"
          allowFullScreen
          // @ts-ignore
          ref={ref || iframeRef}
        />
      </div>
    );
  },
);

export default VideoPlayer;
